import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { first, map, Observable, ReplaySubject, startWith } from 'rxjs';
import {
  completeSubject,
  defineUserType,
  getOrganizationTypeProfileInfo,
  normalizeWebsiteLink,
  promiseWithCatch,
} from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import { ValueTypes } from 'src/app/common/custom.types';
import ProjectLinks from 'src/app/common/project.links';
import { ChatConversation } from 'src/app/components/chat/interfaces/chat-model.interface';
import { ChatSendMessageToService } from 'src/app/components/chat/services/chat-send-message-to.service';
import { getLocation, LocationQueryOptions } from 'src/app/components/map/shared/location.service';
import FieldAllowedValueClasses from 'src/app/forms/fields/field-allowed-value-classes.values';
import { Attribute } from 'src/app/interfaces/form-and-field.interface';
import {
  AddInfoFieldContent,
  AddSpecificModelFieldsContent,
  ProfileInfo,
  RoleDisplayedInfo,
  UserBasicInfoContent,
  UserTypeContent,
  ViewProfileContent,
} from 'src/app/interfaces/profile.interface';
import { ItemStatus } from 'src/app/interfaces/views.interface';
import { RoleInfo } from 'src/app/models/role-info.model';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { FormStoreService } from 'src/app/services/form-store.service';
import { FullUser, LocationObject, OrganizationType } from 'src/app/swagger-api';
import { CardInfoType, IndividualRoles, ProfileTypes, ProfileViewAttr, UserType, UserTypes } from '../../profile.enum';
import { aliasMetadata, verifiedEmailMetadata } from '../edit-profile/profile-forms.metadata';
import { getStatus } from './regular-user-profile/regular-user-profile-helper';

@Injectable()
export class ViewProfileService implements OnDestroy {
  private fieldsVsBasicInfoChanges$: ReplaySubject<ViewProfileContent> = new ReplaySubject();

  private readonly userTypeData: {
    [key: string]: (content: UserTypeContent) => void;
  } = {
    individual: (content: UserTypeContent) => this.initIndividualUserData(content),
    organization: (content: UserTypeContent) => this.initOrganizationData(content),
  };

  private notificationService = inject(NotificationService);
  private formStoreService = inject(FormStoreService);
  private router = inject(Router);
  private chatSendMessageToService = inject(ChatSendMessageToService);

  public ngOnDestroy(): void {
    completeSubject(this.fieldsVsBasicInfoChanges$);
  }

  public get fieldsChanges(): Observable<ViewProfileContent> {
    return this.fieldsVsBasicInfoChanges$.pipe(
      startWith({ fields: [], userBasicInfo: { avatar: '', displayedName: '', status: ItemStatus.EMPTY, data: [] } }),
    );
  }

  public seeMoreProfileInfo(fields: ProfileInfo[], displayedName: string): void {
    this.notificationService
      .dialogUserInfoDisplay({ title: displayedName, userInfo: fields })
      .pipe(first())
      .subscribe();
  }

  public sendMessage(user: FullUser): Observable<string> {
    return this.chatSendMessageToService
      .sendMessageToProfiles([user], true /* showConfirmation */)
      .pipe(map((conversation: ChatConversation) => conversation.channel.channel_external_id));
  }

  /**
   * @description Helps to convert user info from model view to hint-value list, to
   *              fetch user's roles list and its main properties.
   * @param { boolean } isOwner
   */
  public setupProfileInfo(isOwner: boolean, userInfo: FullUser, userRoles: RoleInfo[]): void {
    const user: FullUser = structuredClone(userInfo);

    if (!user) {
      promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      return;
    }

    const type: string = defineUserType(user);
    const fields: ProfileInfo[] = [];
    const userBasicInfo: UserBasicInfoContent = {
      avatar: user.image_url || AppSettings.NO_PHOTO_DEFAULT_IMAGE,
      displayedName: user.displayed_name,
      status: getStatus(user),
      data: [],
    };

    this.setupMainProfilePart(user, fields);

    if (type !== UserTypes.NONE) {
      this.userTypeData[type]({ user, fields, userBasicInfo, isOwner, userRoles });
    }

    this.fieldsVsBasicInfoChanges$.next({
      fields: fields.filter((field: ProfileInfo) => field.value),
      userBasicInfo,
    });
  }

  private setupMainProfilePart(user: FullUser, fields: ProfileInfo[]): void {
    const mainAttr: Attribute[] = this.formStoreService.metadataList.get(ProfileTypes.MAIN);

    const emailIndex: number = mainAttr.findIndex((attr: Attribute) => attr.name === verifiedEmailMetadata.name);

    const aliasIndex: number = mainAttr.findIndex((attr: Attribute) => attr.name === aliasMetadata.name);

    if (user.profile) {
      this.addInfoField({
        dataObject: user.profile,
        fields,
        attribute: mainAttr[emailIndex],
      });
    }

    if (user.individual) {
      this.addInfoFieldArtificial(
        {
          dataObject: user.individual,
          fields,
          attribute: mainAttr[aliasIndex],
        },
        user.displayed_name,
      );
    }

    this.addSpecificModelFields({
      model: user.profile,
      attributes: mainAttr.slice(aliasIndex + 1),
      fields,
    });
  }

  /**
   * @description Adds general and specific roles info in hint-value list.
   * @param { UserTypeContent } type
   * @param { string } content
   */
  private initUserTypeFields(content: UserTypeContent, type: string): void {
    const userTypeAttrs: Attribute[] = this.formStoreService.metadataList.get(type);
    this.addSpecificModelFields({
      model: content.user[type] as object,
      attributes: userTypeAttrs,
      fields: content.fields,
    });

    const additionalProfileAttrs: Attribute[] = this.formStoreService.metadataList.get(ProfileViewAttr.ADDITIONAL);
    this.addSpecificModelFields({
      model: content.user.profile,
      attributes: additionalProfileAttrs,
      fields: content.fields,
    });
  }

  /**
   * @description Forms user info for organization user type.
   * @param { UserTypeContent } content
   */
  private initOrganizationData(content: UserTypeContent): void {
    if (content.user.organization) {
      content.fields.push({
        hint: 'PROFILE_PROFILE_INFO_ROLE_HINT',
        value: 'PROFILE_PROFILE_INFO_ROLE_ORGANIZATION_HINT',
      });

      this.setupOrganizationBasicInfo(content);

      this.initUserTypeFields(content, UserTypes.ORGANIZATION);
    }
  }

  /**
   * @description Forms user info for individual user type.
   * @param { UserTypeContent } content
   */
  private initIndividualUserData(content: UserTypeContent): void {
    if (content.user.individual) {
      this.initUserTypeFields(content, UserTypes.INDIVIDUAL);

      content.userBasicInfo.data = this.setupDisplayedRoles(content);

      this.setupIndividualRolesInfo(content);
    }
  }

  /**
   * @description Forms user info for organization user type basic info.
   * @param { UserTypeContent } content
   */
  private setupOrganizationBasicInfo(content: UserTypeContent): void {
    content.userBasicInfo.displayedName = content.user.displayed_name;

    content.userBasicInfo.isOrganization = true;

    content.userBasicInfo.data.push({
      userType: UserType.organization,
      organization: {
        organization_type: content.user.organization.organization_type,
        website: content.user.organization.website,
        specialties: content.user.organization.specialty,
        diseases: content.user.organization.diseases,
      },
    });
  }

  /**
   * @description Forms list of individual user type roles.
   * @returns { string }
   */
  private setupDisplayedRoles(content: UserTypeContent): RoleDisplayedInfo[] {
    const sortOrder: string[] = [
      IndividualRoles.patient,
      IndividualRoles.doctor,
      IndividualRoles.med_staff,
      IndividualRoles.researcher,
    ];

    const roles: string[] = content.userRoles.map((role: RoleInfo) => role.label);
    const displayRoles: RoleDisplayedInfo[] = [];

    roles
      .sort((a: string, b: string) => {
        return sortOrder.indexOf(a) - sortOrder.indexOf(b);
      })
      .forEach((role: string) => {
        displayRoles.push(this.getRolePrecondition(content, role));
      });

    return displayRoles;
  }

  private getRolePrecondition(content: UserTypeContent, role: string): RoleDisplayedInfo {
    switch (role) {
      case IndividualRoles.patient:
        return {
          userType: UserType.patient,
          patient: content.user.patient,
        };

      case IndividualRoles.doctor:
        return {
          userType: UserType.doctor,
          doctor: content.user.doctor,
        };
      case IndividualRoles.med_staff:
        return {
          userType: UserType.med_staff,
          med_staff: content.user.med_staff,
        };
      case IndividualRoles.researcher:
        return {
          userType: UserType.researcher,
          researcher: content.user.researcher,
        };
      default:
        throw new Error(`Unsupported role: ${role}`);
    }
  }

  /**
   * @description Forms user info for roles of individual user type.
   * @param { UserTypeContent } content
   */
  private setupIndividualRolesInfo(content: UserTypeContent): void {
    content.userRoles.forEach((role: RoleInfo) => {
      this.addRoleFieldsInfo({ ...content, role });
    });
  }

  /**
   * @description Forms basic and extended user info by its roles details. Forms a list
   *              of users roles. Forms a list of hint-values. Adds a role naming
   *              at the beginning of each part of role properties/fields list.
   * @param { UserTypeContent } content
   */
  private addRoleFieldsInfo(content: UserTypeContent): void {
    if (content.user[content.role.value]) {
      content.fields.push({
        hint: 'PROFILE_PROFILE_INFO_ROLE_HINT',
        value: content.role.label,
      });

      const roleAttr: Attribute[] = this.formStoreService.metadataList.get(content.role.value);
      this.addSpecificModelFields({
        model: content.user[content.role.value] as object,
        attributes: roleAttr,
        fields: content.fields,
      });
    }
  }

  /**
   * @description Helps to add to list a specific user roles info like individual model
   *              for individual part and organization role info for organization user type.
   * @param { UserTypeContent } content
   */
  private addSpecificModelFields(content: AddSpecificModelFieldsContent): void {
    if (content.attributes.length > 0 && content.model) {
      content.attributes.forEach((attribute: Attribute) => {
        this.addInfoField({
          dataObject: content.model,
          fields: content.fields,
          attribute,
        });
      });
    }
  }

  /**
   * @description Forms hint-value association.
   * @param { AddInfoFieldContent } content
   */
  private addInfoField(content: AddInfoFieldContent): void {
    if (content.dataObject[content.attribute.name]) {
      this.addInfoFieldWithValue(content, this.defineValue(content));
    }
  }

  private addInfoFieldArtificial(content: AddInfoFieldContent, rawValue: ValueTypes): void {
    const value: ProfileInfo = this.defineGivenValue(content, rawValue);
    if (value) {
      this.addInfoFieldWithValue(content, value);
    }
  }

  private addInfoFieldWithValue(content: AddInfoFieldContent, value: ProfileInfo): void {
    content.fields.push({
      hint: content.attribute.view_label || content.attribute.label || content.attribute.placeholder,
      ...value,
    });
  }

  /**
   * @description Forms the displayed field value depend on attribute's value_class_front
   *              and additional value properties.
   * @param { AddInfoFieldContent } content
   * @returns { ProfileInfo }
   */
  private defineValue(content: AddInfoFieldContent): ProfileInfo {
    const value = content.dataObject[content.attribute.name] as ValueTypes;
    return this.defineGivenValue(content, value);
  }

  private defineGivenValue(content: AddInfoFieldContent, value: ValueTypes): ProfileInfo {
    const valueClassValue: ProfileInfo = this.defineValueByValueClass(content, value);

    if (valueClassValue !== null) {
      return valueClassValue;
    }

    if (value instanceof Array) {
      return { value, type: CardInfoType.list };
    }

    if (content.attribute.visual_format) {
      return { value: content.attribute.visual_format(value.toString()) };
    }

    return { value: value.toString() };
  }

  /**
   * @description Returns the customized field's value depend on incoming value_class_front.
   * @param { AddInfoFieldContent } content
   * @param { ValueTypes } value
   * @returns { ProfileInfo }
   */
  private defineValueByValueClass(content: AddInfoFieldContent, value: ValueTypes): ProfileInfo {
    switch (content.attribute.value_class_front) {
      case FieldAllowedValueClasses.YES_NO_SELECT:
        return { value: value === 'YES' ? 'YES_HINT' : 'NO_HINT' };

      case FieldAllowedValueClasses.URL:
        return {
          value: `<a href='${normalizeWebsiteLink(value.toString())}' target='_blank'>${value.toString()}</a>`,
        };

      case FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT:
        return {
          value,
          type: CardInfoType.organizationList,
        };

      case FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT:
        return getOrganizationTypeProfileInfo(value as OrganizationType);

      case FieldAllowedValueClasses.LOCATION:
      case FieldAllowedValueClasses.LOCATION_ALL_TYPES:
        return {
          value: getLocation(
            value as LocationObject,
            {
              includeStreet: true,
            } as LocationQueryOptions,
          ),
        };

      case FieldAllowedValueClasses.DISEASES_SELECT:
      case FieldAllowedValueClasses.SPECIALTIES_SELECT:
        return {
          value: value as string[],
          type: CardInfoType.list,
        };

      default:
        return null;
    }
  }
}
